import { i18n } from '@lingui/core'
import { Form, Formik } from 'formik'
import { useMemo } from 'react'
import { useSetRecoilState } from 'recoil'
import styled, { useTheme } from 'styled-components'

import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeaderAction } from '@/@bothrs-ui/web/components/navigation/header/AppHeaderAction'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { globalDefinitions } from '@/defined-translations'
import { mixpanel } from '@/mixpanel'
import { useLoginAndRegister } from '@/modules/healthblocks/hooks/useLogin'
import { elapsedTimeAtom } from '@/modules/idle-timer/state/idle-timer.atoms'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { StorageKeys, StorageService } from '@/utils/storageService'
import { LoginFormSchema } from './LoginForm.schema'

import type { AuthState } from '@healthblocks-io/core/auth-context'
import type { FormikHelpers } from 'formik'

type LoginFormProps = {
  onError?: (error: unknown) => void
  onSuccess?: (authState: AuthState) => void

  onGoBack?: () => void
}

type FormValues = {
  email: string
  password: string
}

const LoginForm = ({ onError, onSuccess, onGoBack }: LoginFormProps) => {
  const { loginWithAsMandate } = useLoginAndRegister()
  const theme = useTheme()
  const { setLockout } = useLockoutTime()
  const setLastInteraction = useSetRecoilState(elapsedTimeAtom)

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleSubmit(values: FormValues, actions: FormikHelpers<FormValues>) {
    actions.setSubmitting(true)

    if (values.email === '') {
      //login for mandate
    } else {
      loginWithAsMandate({ email: values.email, password: values.password })
        .then((result) => {
          mixpanel.track('account_login')

          setLockout(false)
          // Reset idle timer on successful login to avoid instantly locking again
          // due to a persisted "last interaction" timestamp from a previous session.
          setLastInteraction(Date.now())

          // Store last logged in timestamp
          StorageService.setItem(StorageKeys.LastLoggedInAt, Date.now())
          onSuccess?.(result)
        })
        .catch((error) => {
          actions.setFieldError('email', '')
          actions.setFieldError('password', 'E-mailadres of wachtwoord niet correct.')

          setLockout(true)

          if (onError) {
            onError(error)
          }
        })
        .finally(() => {
          actions.setSubmitting(false)
        })
    }
  }

  // Template variables
  // ------------------------------------------------------------------------- /
  const formikInitialValues: FormValues = useMemo<FormValues>(
    () => ({
      email: '',
      password: '',
    }),
    [],
  )

  return (
    <StyledLoginFormRoot gap={theme.UI.Spacing.Space4}>
      {onGoBack && (
        <AppHeaderAction
          action={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: onGoBack,
          }}
        />
      )}
      <Formik initialValues={formikInitialValues} validationSchema={LoginFormSchema} onSubmit={handleSubmit}>
        {({ isValid, isSubmitting }) => (
          <Form data-test-id='hidden-login-form'>
            <Input label='E-mailadres' name='email' type='email' placeholder='naam@provider.be' />

            <Input label='Wachtwoord' name='password' type='password' placeholder='Typ je wachtwoord' />

            <Button type='submit' variant='Primary' disabled={isSubmitting || !isValid} isLoading={isSubmitting}>
              Aanmelden
            </Button>
          </Form>
        )}
      </Formik>
    </StyledLoginFormRoot>
  )
}

export { LoginForm }

const StyledLoginFormRoot = styled(FlexColumn)`
  width: 100%;
`
